import type { Wager, WagersResponse } from "../shared/wagers.ts";
import type { RouletteResponse, RouletteSpin } from "../shared/roulette.ts";
import type { YoursReminderSent } from "../shared/yours-push.ts";
import type { CheerNews } from "../shared/cheers.ts";
import { noteWallpaper } from "./wallpaper.ts";
import type {
  AuthState,
  HouseState,
  Invite,
  PasskeyOptions,
  PasskeyRemoved,
  Role,
  BoardSummary,
  ClaimsSummary,
  ClientEvent,
  ClientTaskEventKind,
  CreateNoteBody,
  CreateRewardBody,
  CreateScheduleBody,
  CreateTaskBody,
  FlagKey,
  Flags,
  FlashEvent,
  LateRush,
  FlashState,
  GiftLink,
  HallMonth,
  Note,
  PeonList,
  PeonRenameResult,
  RewardClaim,
  Schedule,
  Task,
  TaskStatus,
  TopPeon,
  PeonStats,
  Redemption,
  RewardsSummary,
  PursePage,
  TeslaGrant,
  TeslaStatus,
  TeslaVehicle,
  UpdateScheduleBody,
  UpdateTaskBody,
  UsageStats,
  CreateUpgradeBody,
  PledgeResult,
  RecapSummary,
  Upgrade,
  UpgradesSummary,
  UpgradeView,
  WoodBalance,
  WoodOrder,
  WoodOrders,
  WoodSettings,
  WoodTradeResult,
} from "../shared/types";
import type { FlashPickState } from "../shared/flash.ts";
import type { Reward } from "../shared/rewards";
import type { AchievementsResponse, BadgeBackfillResponse } from "../shared/achievements.ts";
import type { TrinketFind, TrinketsResponse } from "../shared/trinkets.ts";
import type { SetSpeedrunBody, SpeedrunState } from "../shared/speedrun.ts";
import type { SlopTally } from "../shared/slop.ts";
import type { FeedbackDraft, FeedbackSent } from "../shared/feedback.ts";
import type { CreateWeeklyEntryBody, UpdateWeeklyEntryBody, WeeklyEntriesResponse, WeeklyEntry, WeeklyResponse, WeeklyWriteOpenResponse } from "../shared/weekly.ts";
import type { PeonAppearance } from "../shared/shirts.ts";
import { parseServerTiming, templateRoute, type PerfSummary } from "../shared/perf.ts";
import { deviceTimezone, getPeonName } from "./store.ts";
import { memberHeaders } from "./members-ui.ts";
import { isNameHeldBody } from "../shared/passwords.ts";
import { recordApi } from "./perf.ts";
import { getLocalFlag } from "./flags-core.ts";
import { FLAG_PREVIEW_HEADER, flagPreviewValue } from "../shared/flags.ts";
import { nextLookGeneration, noteHallLooks, noteSummaryLooks } from "./look-rows.ts";
import { startOfLocalDay } from "../shared/claims.ts";

/** GET /api/peons/wardrobe: the look, the pieces owned (ids from shared/cosmetics.ts), the gold held, and whether pieces cost gold here. */
export interface Wardrobe {
  look: PeonAppearance;
  owned: string[];
  balance: number;
  shop: boolean;
}

/** POST /api/peons/wardrobe/buy answers with the wardrobe as it now is, less the shop flag. */
export type WardrobePurchase = Omit<Wardrobe, "shop">;

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** Subscribe to the signed-out 401 ("Not signed in") so the app can drop to the login screen. A wrong password is a form error, not this. */
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

const nameHeldListeners = new Set<(name: string) => void>();
/**
 * Subscribe to 403 name_held answers (docs/members.md "Personal passwords"): the name this device
 * uses is held by someone it is not signed in as. The app forgets the stored name and shows the
 * name gate with that name and its password field. `name` is the held name's registered spelling.
 */
export function onNameHeld(fn: (name: string) => void): () => void {
  nameHeldListeners.add(fn);
  return () => nameHeldListeners.delete(fn);
}

/** Status used for a request that never reached the server (offline, DNS, aborted), so callers can tell it from a server answer. */
export const NO_CONNECTION = 0;
export const NO_CONNECTION_MESSAGE = "No connection. Check your internet and try again.";
/** A failed answer with no words of its own (no JSON error, and no status text over HTTP/2). */
export const FALLBACK_ERROR_MESSAGE = "Something went wrong. Try again.";
/** The middleware's own words for a device with no session (functions/_middleware.ts); only this 401 signs the device out. */
export const SIGNED_OUT_ERROR = "Not signed in";
/** A JSON call that has not answered in this long is given up as no connection. Photo uploads have no limit. */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Whether a 401 means the device is signed out (drop to the gate) rather than a form's wrong
 * password ("Wrong password", "Wrong current password"), which reaches the caller as an error.
 */
export function isSignedOutAnswer(status: number, error: string | undefined): boolean {
  return status === 401 && error === SIGNED_OUT_ERROR;
}

/** The message a failed answer shows: the server's words, else the status text, else the plain fallback. */
export function failureMessage(error: string | undefined, statusText: string): string {
  if (typeof error === "string" && error.trim()) return error;
  if (statusText.trim()) return statusText;
  return FALLBACK_ERROR_MESSAGE;
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs: number | null = REQUEST_TIMEOUT_MS): Promise<T> {
  const start = performance.now();
  const method = init.method ?? "GET";
  const controller = timeoutMs === null ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs!) : null;
  const noConnection = (status: number | null) => {
    recordApi(templateRoute(path), method, performance.now() - start, { app: null, d1: null, d1exec: null, queries: null }, status ?? NO_CONNECTION);
    return new ApiError(NO_CONNECTION, NO_CONNECTION_MESSAGE);
  };
  try {
    let res: Response;
    try {
      // Every request says who is asking and from where (docs/members.md): the server keeps the
      // device's row current with them. The only place these headers are set.
      const headers = new Headers(init.headers);
      for (const [k, v] of Object.entries(memberHeaders(getPeonName()))) headers.set(k, v);
      // This device's own switch for a previewable flag (shared/flags.ts PREVIEW_FLAGS), read
      // synchronously from the local store on every request: the server shows this device alone
      // what the household switch would show everyone (docs/product.md "Season recap").
      const preview = flagPreviewValue(getLocalFlag);
      if (preview) headers.set(FLAG_PREVIEW_HEADER, preview);
      res = await fetch(path, { credentials: "same-origin", ...init, headers, ...(controller ? { signal: controller.signal } : {}) });
    } catch {
      // fetch rejects (TypeError: "Failed to fetch" / "Load failed", or the timeout's abort) only
      // when no response came back at all.
      throw noConnection(null);
    }
    const timing = parseServerTiming(res.headers.get("server-timing"));
    if (!res.ok) {
      let error: string | undefined;
      try {
        const body = (await res.json()) as { error?: string };
        if (typeof body?.error === "string") error = body.error;
        if (res.status === 403 && isNameHeldBody(body)) nameHeldListeners.forEach((fn) => fn(body.name));
      } catch {
        /* not JSON, or the body timed out: the status still says what happened */
      }
      if (isSignedOutAnswer(res.status, error)) unauthorizedListeners.forEach((fn) => fn());
      recordApi(templateRoute(path), method, performance.now() - start, timing, res.status);
      throw new ApiError(res.status, failureMessage(error, res.statusText));
    }
    let body: T;
    try {
      body = (await res.json()) as T;
    } catch (e) {
      if (controller?.signal.aborted) throw noConnection(res.status);
      throw e;
    }
    recordApi(templateRoute(path), method, performance.now() - start, timing, res.status);
    return body;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

function jsonInit(method: string, body: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}

export const api = {
  auth: {
    me: () => request<AuthState>("/api/auth/me"),
    login: (password: string) => request<{ ok: true } & AuthState>("/api/auth/login", jsonInit("POST", { password })),
    logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
    switch: (id: string) => request<AuthState>("/api/auth/switch", jsonInit("POST", { id })),
    /** Joins the invite's household with the invite's role; it becomes the active one (docs/members.md). */
    join: (code: string) => request<AuthState>("/api/auth/join", jsonInit("POST", { code })),
    /** Founds a household with this device as its first steward; it becomes the active one. */
    create: (body: { name: string; password: string; code?: string }) => request<AuthState>("/api/auth/create", jsonInit("POST", body)),
    /** Signs in as the person of `name` with their own password; their household becomes the active one (docs/members.md "Personal passwords"). */
    person: (name: string, password: string) => request<AuthState>("/api/auth/person/login", jsonInit("POST", { name, password })),
  },
  /** Personal passwords: this device's own person's password, set or changed (`current` when it already has one). */
  people: {
    setPassword: (password: string, current?: string) =>
      request<AuthState>("/api/people/password", jsonInit("POST", current === undefined ? { password } : { password, current })),
  },
  /** The House screen (docs/members.md): stewards only; a peon gets 403 "Stewards only". */
  /** People and passkeys (docs/members.md): the ceremonies' two halves, and this device's own passkeys. */
  passkeys: {
    options: (intent: "register" | "login") => request<PasskeyOptions>("/api/auth/passkey/options", jsonInit("POST", { intent })),
    register: (challengeId: string, credential: unknown) =>
      request<AuthState>("/api/auth/passkey/register", jsonInit("POST", { challenge_id: challengeId, credential })),
    login: (challengeId: string, credential: unknown) => request<AuthState>("/api/auth/passkey/login", jsonInit("POST", { challenge_id: challengeId, credential })),
    remove: (credentialId: string) => request<PasskeyRemoved>(`/api/house/passkeys/${encodeURIComponent(credentialId)}`, { method: "DELETE" }),
  },
  house: {
    get: () => request<HouseState>("/api/house"),
    patch: (body: { name?: string; join_by_password?: boolean; roulette_eligible?: string[] | null }) => request<HouseState>("/api/house", jsonInit("PATCH", body)),
    password: (password: string) => request<{ ok: true }>("/api/house/password", jsonInit("POST", { password })),
    invite: (role: Role, maxUses?: number) =>
      request<Invite>("/api/house/invites", jsonInit("POST", maxUses === undefined ? { role } : { role, max_uses: maxUses })),
    revokeInvite: (id: string) => request<HouseState>(`/api/house/invites/${encodeURIComponent(id)}`, { method: "DELETE" }),
    setRole: (sessionId: string, role: Role) => request<HouseState>(`/api/house/members/${encodeURIComponent(sessionId)}`, jsonInit("PATCH", { role })),
    signOutDevice: (sessionId: string) => request<HouseState>(`/api/house/members/${encodeURIComponent(sessionId)}`, { method: "DELETE" }),
    /** A steward removes a person's password; the name is free again until they set one. */
    removePassword: (personId: string) => request<HouseState>(`/api/house/people/${encodeURIComponent(personId)}/password`, { method: "DELETE" }),
    /** Deletes the household when `name` is its name; returns the AuthState that is left. */
    delete: (name: string) => request<AuthState>("/api/house", jsonInit("DELETE", { name })),
  },
  board: {
    /**
     * The board's one request (T5): tasks plus everything else the screen
     * shows, in one D1 batch server-side. `limit`/`before` page the Done tab
     * (F4/T7); `next_before` on the response tells the caller whether to
     * offer "Show older".
     */
    summary: (opts: {
      status: TaskStatus;
      name: string;
      since: string | null;
      /** Start of the previous month: the Peon of the Month header names its winner (`crown`). */
      prev?: string | null;
      claims: boolean;
      flash: boolean;
      notes: boolean;
      limit?: number;
      before?: string;
    }) => {
      const q = new URLSearchParams({ status: opts.status, name: opts.name });
      if (opts.since) q.set("since", opts.since);
      if (opts.prev) q.set("prev", opts.prev);
      if (opts.claims) q.set("claims", "1");
      if (opts.flash) q.set("flash", "1");
      if (opts.notes) q.set("notes", "1");
      if (opts.limit) q.set("limit", String(opts.limit));
      if (opts.before) q.set("before", opts.before);
      // The looks it carries go to src/appearance.ts unless this device changed one since the
      // request started (src/look-rows.ts), so a summary in flight never paints old clothes back.
      const gen = nextLookGeneration();
      return request<BoardSummary>(`/api/board?${q.toString()}`).then((summary) => {
        // The household's wallpaper (src/wallpaper.ts): the shell draws it while wallpaperUI is on.
        noteWallpaper(summary.wallpaper);
        return noteSummaryLooks(summary, gen);
      });
    },
  },
  tasks: {
    /** "Me do it": the task is `name`'s for an hour; someone else's live claim is 409 (functions/api/tasks/[id]/claim.ts). */
    claim: (id: string, name: string) => request<Task>(`/api/tasks/${encodeURIComponent(id)}/claim`, jsonInit("POST", { name })),
    /** "Never mind": lets go of your own claim. */
    release: (id: string, name: string) => request<Task>(`/api/tasks/${encodeURIComponent(id)}/claim`, jsonInit("DELETE", { name })),
    /** Cheers (docs/cheers.md): cheers the done task as `name`, for keeps; answers the task with its cheers (functions/api/tasks/[id]/cheer.ts). */
    cheer: (id: string, name: string) => request<Task>(`/api/tasks/${encodeURIComponent(id)}/cheer`, jsonInit("POST", { name })),
    /** Projects (docs/projects.md): posts a progress photo on an open project as `name`; answers the task with its progress (functions/api/tasks/[id]/progress). */
    addProgress: (id: string, name: string, photoId: string) =>
      request<Task>(`/api/tasks/${encodeURIComponent(id)}/progress`, jsonInit("POST", { name, photo_id: photoId })),
    /** Takes a progress photo off an open project: its poster or a steward; answers the task. */
    removeProgress: (id: string, progressId: string) =>
      request<Task>(`/api/tasks/${encodeURIComponent(id)}/progress/${encodeURIComponent(progressId)}`, { method: "DELETE" }),
    /** One push asking `to` to do it, once a day per task per sender; a second is 429 (functions/api/tasks/[id]/nudge.ts). */
    nudge: (id: string, from: string, to: string) =>
      request<{ sent: number; gone: number; failed: number; to: string; nudged_today: true }>(
        `/api/tasks/${encodeURIComponent(id)}/nudge`,
        jsonInit("POST", { from, to, since: startOfLocalDay() }),
      ),
    /** Whether `from` already nudged about this task today (this device's day). */
    nudged: (id: string, from: string) =>
      request<{ nudged_today: boolean }>(
        `/api/tasks/${encodeURIComponent(id)}/nudge?${new URLSearchParams({ from, since: startOfLocalDay() }).toString()}`,
      ),
    /** `limit`/`before` page the done list (F4/T7); ignored server-side for status "open". */
    list: (status: TaskStatus, opts?: { limit?: number; before?: string }) => {
      const q = new URLSearchParams({ status });
      if (opts?.limit) q.set("limit", String(opts.limit));
      if (opts?.before) q.set("before", opts.before);
      return request<Task[]>(`/api/tasks?${q.toString()}`);
    },
    get: (id: string) => request<Task>(`/api/tasks/${id}`),
    create: (body: CreateTaskBody) => request<Task>("/api/tasks", jsonInit("POST", body)),
    update: (id: string, body: UpdateTaskBody) => request<Task>(`/api/tasks/${id}`, jsonInit("PATCH", { ...body, tz: deviceTimezone() })),
    remove: (id: string) => request<Task>(`/api/tasks/${id}?tz=${encodeURIComponent(deviceTimezone())}`, { method: "DELETE" }),
    /**
     * Reports a client-only task-life moment. Best-effort and fire-and-forget:
     * failures are swallowed, since this is analytics, not app state.
     * `done_abandoned` uses sendBeacon when available, since it fires from
     * flows that can unload the page (closing the photo picker).
     */
    event: (id: string, kind: ClientTaskEventKind, name: string): void => {
      const payload = JSON.stringify({ kind, name });
      try {
        if (kind === "done_abandoned" && typeof navigator !== "undefined" && navigator.sendBeacon) {
          navigator.sendBeacon(`/api/tasks/${id}/events`, new Blob([payload], { type: "application/json" }));
          return;
        }
        void fetch(`/api/tasks/${id}/events`, { method: "POST", headers: { "content-type": "application/json" }, body: payload, credentials: "same-origin" }).catch(() => {});
      } catch {
        /* best-effort */
      }
    },
  },
  schedules: {
    list: () => request<Schedule[]>("/api/schedules"),
    create: (body: CreateScheduleBody) => request<Schedule>("/api/schedules", jsonInit("POST", body)),
    update: (id: string, body: UpdateScheduleBody) => request<Schedule>(`/api/schedules/${id}`, jsonInit("PATCH", body)),
    remove: (id: string) => request<Schedule>(`/api/schedules/${id}`, { method: "DELETE" }),
    completeEarly: (id: string, body: { done_by: string; done_photo_id: string }) =>
      request<Task>(`/api/schedules/${id}/complete-early`, jsonInit("POST", body)),
  },
  /** GET /api/achievements: every peon's badges in the household (docs/achievements.md). */
  achievements: {
    list: () => request<AchievementsResponse>("/api/achievements"),
    /** POST /api/achievements/backfill (stewards): every peon's whole record checked now for badges history already earned. */
    backfill: () => request<BadgeBackfillResponse>("/api/achievements/backfill", { method: "POST" }),
  },
  /** GET /api/trinkets: the household's trinkets (docs/trinkets.md); POST /api/trinkets/dig (stewards): a find for the caller now. */
  trinkets: {
    list: () => request<TrinketsResponse>("/api/trinkets"),
    dig: () => request<{ find: TrinketFind | null }>("/api/trinkets/dig", { method: "POST" }),
  },
  stats: {
    peon: (name: string, since: string | null) =>
      request<PeonStats>(`/api/stats/peon?name=${encodeURIComponent(name)}${since ? `&since=${encodeURIComponent(since)}` : ""}`),
    topPeons: (since: string | null, limit = 1) =>
      request<TopPeon[]>(`/api/stats/top-peons?limit=${limit}${since ? `&since=${encodeURIComponent(since)}` : ""}`),
    /** Peon of the Month for every month on record, newest first (F4/T7): computed server-side instead of downloading the whole done list. */
    hall: (tz?: string) => {
      // The winners' looks go to src/appearance.ts by the board summary's rule (src/look-rows.ts).
      const gen = nextLookGeneration();
      return request<HallMonth[]>(`/api/stats/hall${tz ? `?tz=${encodeURIComponent(tz)}` : ""}`).then((months) => noteHallLooks(months, gen));
    },
    /** The household usage dashboard (src/views/Usage.tsx): one D1 batch server-side, see server/usage.ts. */
    usage: (days: 7 | 30 | 90 | 365) => request<UsageStats>(`/api/stats/usage?days=${days}`),
  },
  /**
   * The season recap (src/recap.ts). The video and poster load through signed links (`link`): a
   * <video> or <img> cannot send x-flag-preview, and a signature opens the bytes on its own.
   */
  recaps: {
    /** A six hour signed link to the sd or hd cut, or to the poster. */
    link: (id: string, q: "sd" | "hd" | "poster") =>
      request<{ url: string; expiresAt: string }>(`/api/recaps/${encodeURIComponent(id)}/link?q=${q}`),
    get: (id: string) => request<RecapSummary>(`/api/recaps/${encodeURIComponent(id)}`),
    /** Idempotent: this peon has seen or skipped it. Best-effort; the device mark is what counts offline. */
    seen: (id: string) => request<{ ok: true }>(`/api/recaps/${encodeURIComponent(id)}/seen`, { method: "POST" }),
  },
  push: {
    publicKey: () => request<{ key: string }>("/api/push/public-key"),
    subscribe: (subscription: PushSubscriptionJSON, name: string) =>
      request<{ ok: true }>("/api/push/subscribe", jsonInit("POST", { subscription, name })),
    unsubscribe: (endpoint: string) => request<{ ok: true }>("/api/push/subscribe", jsonInit("DELETE", { endpoint })),
    /** The iOS app's APNs device token (src/native-push.ts), stored beside the web subscriptions. */
    native: (token: string, name: string) =>
      request<{ ok: true }>("/api/push/native", jsonInit("POST", { token, name, platform: "ios" })),
    forgetNative: (token: string) => request<{ ok: true }>("/api/push/native", jsonInit("DELETE", { token })),
    test: () => request<{ sent: number; gone: number; failed: number }>("/api/push/test", { method: "POST" }),
  },
  photos: {
    upload: (blob: Blob) =>
      request<{ id: string; size: number }>("/api/photos", { method: "POST", headers: { "content-type": blob.type }, body: blob }, null),
    uploadThumb: (id: string, blob: Blob) =>
      request<{ ok: true; size: number }>(`/api/photos/${id}/thumb`, { method: "PUT", headers: { "content-type": blob.type }, body: blob }, null),
    url: (id: string) => `/api/photos/${id}`,
    thumbUrl: (id: string) => `/api/photos/${id}?size=thumb`,
  },
  client: {
    events: (limit = 50) => request<ClientEvent[]>(`/api/client/events?limit=${limit}`),
  },
  /** The purse (flag `purseUI`): a page of this device's peon's gold ledger, newest first; `before` is the previous page's `next`. */
  purse: (before?: string | null) => request<PursePage>(before ? `/api/purse?before=${encodeURIComponent(before)}` : "/api/purse"),
  /** Wagers (flag `wagerUI`, docs/wagers.md): this device's peon's offered and open wagers and the last few finished; offer one; answer one. */
  wagers: {
    list: () => request<WagersResponse>("/api/wagers"),
    offer: (to: string, stake: number) => request<{ wager: Wager }>("/api/wagers", jsonInit("POST", { to, stake })),
    answer: (id: string, action: "accept" | "decline") => request<{ wager: Wager }>(`/api/wagers/${encodeURIComponent(id)}`, jsonInit("POST", { action })),
  },
  /** Task roulette (flag `rouletteUI`, docs/roulette.md): the household's spins, newest first. */
  roulette: {
    list: () => request<RouletteResponse>("/api/roulette"),
    /** The workshop's "Spin the wheel now": the oldest task the sweep would spin, spun now (409 when none). */
    spin: () => request<{ spin: RouletteSpin }>("/api/roulette/spin", { method: "POST" }),
  },
  /** The daily reminder (flag `yoursPushUI`, docs/roulette.md "The daily reminder"). */
  yours: {
    /** The workshop's "Send the yours reminder now": the caller's own reminder, now (409 when nothing is theirs). */
    remind: () => request<YoursReminderSent>("/api/yours/remind", { method: "POST" }),
  },
  /** The weekly scroll (flag `weeklyUI`, docs/weekly.md): the household's newest, or null before the first Sunday. */
  cheers: {
    /** The cheer sheets' read (docs/cheers.md): jobs others did that this peon has not cheered, and cheers on theirs (functions/api/cheers/news.ts). */
    news: () => request<CheerNews<Task>>("/api/cheers/news"),
  },
  weekly: {
    latest: () => request<WeeklyResponse>("/api/weekly"),
    /** This week's writing window and the lines written so far (docs/weekly.md "Writing on it"). */
    entries: () => request<WeeklyEntriesResponse>("/api/weekly/entries"),
    add: (body: CreateWeeklyEntryBody) => request<WeeklyEntry>("/api/weekly/entries", jsonInit("POST", body)),
    /** Changes a line in place (PATCH); a line is never taken off. */
    update: (id: string, body: UpdateWeeklyEntryBody) => request<WeeklyEntry>(`/api/weekly/entries/${encodeURIComponent(id)}`, jsonInit("PATCH", body)),
    /** The workshop's "Open the scroll for writing" (stewards): when it closes on its own, or null while off. */
    writeOpen: () => request<WeeklyWriteOpenResponse>("/api/weekly/write-open"),
    setWriteOpen: (open: boolean) => request<WeeklyWriteOpenResponse>("/api/weekly/write-open", jsonInit("POST", { open })),
  },
  rewards: {
    summary: (name: string) => request<RewardsSummary>(`/api/rewards?name=${encodeURIComponent(name)}`),
    create: (body: CreateRewardBody) => request<Reward>("/api/rewards", jsonInit("POST", body)),
    retire: (id: string, name: string) => request<Reward>(`/api/rewards/${id}`, jsonInit("DELETE", { name })),
    redeem: (name: string, rewardId: string) =>
      request<{ balance: number; redemption: Redemption; claim: RewardClaim; tesla?: TeslaGrant | null }>(
        "/api/rewards/redeem",
        jsonInit("POST", { name, reward_id: rewardId }),
      ),
    claims: (name: string) => request<ClaimsSummary>(`/api/rewards/claims?name=${encodeURIComponent(name)}`),
    claimReceived: (id: string, name: string) => request<RewardClaim>(`/api/rewards/claims/${id}/received`, jsonInit("POST", { name })),
    gifts: () => request<{ gifts: GiftLink[] }>("/api/rewards/gifts"),
    addGift: (body: { reward_id: string; link: string; added_by: string }) => request<GiftLink>("/api/rewards/gifts", jsonInit("POST", body)),
    removeGift: (id: string) => request<{ ok: true }>(`/api/rewards/gifts/${id}`, { method: "DELETE" }),
  },
  /** Dynasty upgrades (docs/upgrades.md). */
  upgrades: {
    /** `wallpaper`: the screen shows the Wallpaper card, so the next wallpaper comes back with the list. */
    list: (name: string, opts: { wallpaper?: boolean } = {}) =>
      request<UpgradesSummary>(`/api/upgrades?name=${encodeURIComponent(name)}${opts.wallpaper ? "&wallpaper=1" : ""}`),
    get: (id: string, name: string) => request<UpgradeView>(`/api/upgrades/${encodeURIComponent(id)}?name=${encodeURIComponent(name)}`),
    create: (body: CreateUpgradeBody) => request<Upgrade>("/api/upgrades", jsonInit("POST", body)),
    pledge: (id: string, name: string, amount: number) => request<PledgeResult>(`/api/upgrades/${encodeURIComponent(id)}/pledge`, jsonInit("POST", { name, amount })),
    built: (id: string, name: string, photoId: string) =>
      request<UpgradeView>(`/api/upgrades/${encodeURIComponent(id)}/built`, jsonInit("POST", { name, photo_id: photoId })),
    remove: (id: string, name: string) => request<{ upgrade: Upgrade; refunded: number }>(`/api/upgrades/${encodeURIComponent(id)}`, jsonInit("DELETE", { name })),
  },
  /** Wood, the currency upgrades are built from (docs/upgrades.md, docs/wood.md): set in the workshop, or bought. */
  wood: {
    balance: (name: string) => request<WoodBalance>(`/api/wood?name=${encodeURIComponent(name)}`),
    /** Sets `name`'s wood to exactly `amount`; `by` is the peon at the workshop. */
    set: (name: string, amount: number, by: string) => request<WoodBalance>("/api/wood", jsonInit("POST", { name, amount, by })),
    /** Trades gold for one pack (docs/wood.md). 409 when short. */
    buy: (name: string) => request<WoodTradeResult>("/api/wood/buy", jsonInit("POST", { name })),
    /** Open e-transfer requests (the workshop's list), plus `name`'s own recent ones when given. */
    orders: (name?: string) => request<WoodOrders>(name ? `/api/wood/orders?name=${encodeURIComponent(name)}` : "/api/wood/orders"),
    /** Asks to buy `wood` wood by e-transfer (shared/wood.ts bounds). 409 when no email is set or one is already waiting. */
    order: (name: string, wood: number) => request<WoodOrder>("/api/wood/orders", jsonInit("POST", { name, wood })),
    /** `name` is whoever marked it, from the workshop. */
    received: (id: string, name: string) => request<WoodOrder>(`/api/wood/orders/${encodeURIComponent(id)}/received`, jsonInit("POST", { name })),
    cancel: (id: string, name: string) => request<WoodOrder>(`/api/wood/orders/${encodeURIComponent(id)}/cancel`, jsonInit("POST", { name })),
    settings: () => request<WoodSettings>("/api/wood/settings"),
    /** Sets where peons send money for wood; an empty string clears it. */
    setEmail: (email: string, by: string) => request<WoodSettings>("/api/wood/settings", jsonInit("PUT", { etransfer_email: email, by })),
  },
  tesla: {
    status: () => request<TeslaStatus>("/api/tesla/status"),
    disconnect: () => request<TeslaStatus>("/api/tesla/disconnect", { method: "POST" }),
    vehicles: () => request<{ vehicles: TeslaVehicle[] }>("/api/tesla/vehicles"),
    selectVehicle: (id: string) => request<TeslaStatus>("/api/tesla/vehicle", jsonInit("POST", { id })),
    drivers: () => request<{ drivers: { id: string; name: string }[] }>("/api/tesla/drivers"),
    endGrant: (id: string) => request<TeslaGrant>(`/api/tesla/grants/${id}/end`, { method: "POST" }),
  },
  /** The speedrun note (docs/notes.md "The speedrun note"): its state, a hand-set title and time, back to live, and hiding it (stewards). */
  speedrun: {
    get: () => request<SpeedrunState>("/api/speedrun"),
    set: (title: string, time: string) => request<SpeedrunState>("/api/speedrun", jsonInit("PUT", { title, time } satisfies SetSpeedrunBody)),
    live: () => request<SpeedrunState>("/api/speedrun", { method: "DELETE" }),
    hide: (hidden: boolean) => request<{ hidden: boolean }>("/api/speedrun/hidden", jsonInit("PUT", { hidden })),
  },
  /** The slop tally (docs/notes.md "The slop tally"): back to 0, by anyone in the household, after the sign's confirm. */
  slop: {
    reset: () => request<SlopTally>("/api/slop/reset", jsonInit("POST", {})),
  },
  /** An idea or a bug from About's "Suggest something" (docs/feedback.md). */
  feedback: {
    send: (draft: FeedbackDraft) => request<FeedbackSent>("/api/feedback", jsonInit("POST", draft)),
  },
  notes: {
    list: () => request<Note[]>("/api/notes"),
    /** `timer` makes it a timed note (docs/timed-notes.md): how long until it rings, and for whom (null is everyone). */
    /** `photoId` makes it a photo note (docs/notes.md "Photo notes"): `body` is then its caption and may be empty. */
    create: (body: string, createdBy: string, timer?: { ring_in_ms: number; recipients: string[] | null }, photoId?: string | null) =>
      request<Note>("/api/notes", jsonInit("POST", { body, created_by: createdBy, ...(timer ?? {}), ...(photoId ? { photo_id: photoId } : {}) } satisfies CreateNoteBody)),
    remove: (id: string) => request<Note>(`/api/notes/${id}`, { method: "DELETE" }),
    /** Ends a timed note's ring for the whole household (Stop on the card); returns the acknowledged note. */
    ack: (id: string) => request<Note>(`/api/notes/${encodeURIComponent(id)}/ack`, { method: "POST" }),
    /** Hearts the note for this peon (`on`) or takes their heart back (docs/notes.md "Hearts"); answers with the note and its hearts. */
    heart: (id: string, on: boolean) => request<Note>(`/api/notes/${encodeURIComponent(id)}/heart`, { method: on ? "PUT" : "DELETE" }),
  },
  flags: {
    get: () => request<Flags>("/api/flags"),
    set: (key: FlagKey, enabled: boolean) => request<Flags>("/api/flags", jsonInit("PUT", { key, enabled })),
  },
  peons: {
    list: () => request<PeonList>("/api/peons"),
    rename: (from: string, to: string) => request<PeonRenameResult>("/api/peons/rename", jsonInit("POST", { from, to })),
    appearance: (name: string) => request<PeonAppearance>(`/api/peons/appearance?name=${encodeURIComponent(name)}`),
    /** Sends only what changed (any of shirt, hair, beard, accessories, badges); the server keeps the rest (docs/peon-look.md "API"). 403 "You don't own that yet" for an unbought piece, "You haven't earned that one yet." for a badge not earned. */
    setAppearance: (name: string, look: Partial<PeonAppearance>) => request<PeonAppearance>("/api/peons/appearance", jsonInit("PUT", { name, ...look })),
    /** The editor's wardrobe (docs/peon-look.md): the look, the pieces owned, the gold held and whether the shop is selling. */
    wardrobe: (name: string) => request<Wardrobe>(`/api/peons/wardrobe?name=${encodeURIComponent(name)}`),
    /** Buys one piece (shared/cosmetics.ts COSMETICS) with gold; 409 "Not enough gold" when short. Wearing it is a separate PUT. */
    buy: (name: string, item: string) => request<WardrobePurchase>("/api/peons/wardrobe/buy", jsonInit("POST", { name, item })),
  },
  /** The signed-in peon's bounty streak (functions/api/streak/index.ts): days in a row, today counted, and the multiplier they earn. */
  streak: {
    get: () => request<{ days: number; multiplier: number | null }>("/api/streak"),
  },
  flash: {
    state: (name: string) => request<FlashState>(`/api/flash?name=${encodeURIComponent(name)}`),
    start: () => request<FlashEvent>("/api/flash/start", { method: "POST" }),
    /** The workshop's "Start a late rush now": the caller's own late chore rush window, now (docs/bounties.md "Late chore rush"). */
    late: () => request<LateRush>("/api/flash/late", { method: "POST" }),
    /** The gold rush pick (docs/bounties.md "Gold rush pick"): whether this peon picks the next rush, the choices, their pick. */
    pick: (name: string) => request<FlashPickState>(`/api/flash/pick?name=${encodeURIComponent(name)}`),
    /** Picks the next rush's task, once; 409 when already picked or the task is not open. */
    choose: (name: string, taskId: string) => request<FlashPickState>("/api/flash/pick", jsonInit("POST", { name, task_id: taskId })),
  },
  perf: {
    summary: (hours: 1 | 24 | 168 | 720 | 2160) => request<PerfSummary>(`/api/perf/summary?hours=${hours}`),
  },
};
